import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { Text, TextType } from 'ui-components/components/text';
import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';
import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import { AwaitingShipmentPlaceholderActions } from './awaiting-shipment-actions';
import { StoreConnectedAwaitingShipmentPlaceholder } from './awaiting-shipment-store-connected';
import { getRateBrowserDefaults } from '../../../../../components/app-header/rate-browser-button';
import { FilterEditorContextProvider } from '../../../../../components/filter-editor/context';
import {
  ConnectLabelProviderModalContextProvider,
  useConnectLabelProviderContext,
} from '../../../../../components/modals/connect-label-provider-modal/context-provider';
import { RateBrowserModalPortalWrapper } from '../../../../../components/modals/rate-browser-modal/rate-browser-modal-portal-wrapper';
import { useTrackRateBrowser } from '../../../../../components/rate-viewer/use-track-rate-browser';
import { US } from '../../../../../constants/country-codes';
import { useDeactivateDemoStore } from '../../../../../demos/hooks/use-deactivate-demo-store';
import { useIsInDemoStore } from '../../../../../demos/hooks/use-is-in-demo-store';
import { useModal } from '../../../../../hooks/use-modal';
import { UserPermissionService } from '../../../../../services/user-permission-service';
import Global from '../../../../../store/global-selectors';
import { CONTROL } from '../../../../../store/rate-browser/rate-broswer-constants';
import { openRateBrowserModal } from '../../../../../store/rate-browser/rate-browser.actions';
import { EventSourceStrings } from '../../../../../tracking/tracking-constants';
import { WorkflowSteps } from '../../../../../tracking/use-workflow-tracking';
import { WalletRegistrationOrUpdateButton } from '../../../../../wallet/components/wallet-registration-or-update-button';
import { AutomationRulesProvider } from '../../../../settings/automation-rules/context';
import { messages } from '../messages';
import style from '../style.module.less';

/**
 * Main placeholder component for the awaiting shipment page
 * @param {object} props
 * @param {boolean} [props.hasStoreConnected] - Whether a store is connected
 * @returns {React.ReactElement}
 */
const AwaitingShipmentPlaceholder = ({ hasStoreConnected = false }) => {
  const isInDemoStore = useIsInDemoStore();
  const dispatch = useDispatch();
  const userRoles = useSelector(Global.selectUseruserRoles);
  const userHasConfigurationRole = UserPermissionService.userHasConfigurationRole(userRoles);
  const sellerHomeCountry = useSelector(Global.selectSellerHomeCountry);
  const homeCountryIsUS = sellerHomeCountry === US;
  const { open, isOpen, close } = useModal();
  const trackRateBrowser = useTrackRateBrowser();
  const rateBrowserDefaults = useSelector(getRateBrowserDefaults);
  const shouldBypassOpeningRateBrowserModalWithEpic = true;
  const { onConnectLabelProvider, trackCarrierConnectionStep } = useConnectLabelProviderContext();
  const { deactivateDemoStore } = useDeactivateDemoStore();

  const onClickLabelProvider = useCallback(() => {
    onConnectLabelProvider();
    trackCarrierConnectionStep(WorkflowSteps.START, {
      source: EventSourceStrings.ORDERS_GRID_EMPTY_STATE_CONNECT_EXISTING_CARRIERS,
    });
  }, [onConnectLabelProvider, trackCarrierConnectionStep]);

  const onClickRateBrowserOpen = useCallback(() => {
    open();
    dispatch(openRateBrowserModal(rateBrowserDefaults, shouldBypassOpeningRateBrowserModalWithEpic));
    trackRateBrowser(WorkflowSteps.START, {
      source: EventSourceStrings.ORDERS_GRID_EMPTY_STATE,
    });
  }, [dispatch, open, rateBrowserDefaults, shouldBypassOpeningRateBrowserModalWithEpic, trackRateBrowser]);

  const onRateBrowserClose = useCallback(() => {
    close();
    trackRateBrowser(WorkflowSteps.COMPLETE);
  }, [close, trackRateBrowser]);

  const onDeactivateDemoStoreClick = useCallback(() => {
    deactivateDemoStore('awaiting_shipment_placeholder');
  }, [deactivateDemoStore]);

  /**
   * Link component for carrier connection
   * @param {object} props
   * @param {React.ReactNode} props.chunks - Content to display in the link
   * @returns {React.ReactElement}
   */
  const CarrierLink = ({ chunks }) => {
    if (!userHasConfigurationRole) {
      return (
        <Tooltip>
          <Text className={style['disabled-text']} element='span' value={chunks} />
          <TooltipContent>
            <Text value={messages.contactAdmin} />
          </TooltipContent>
        </Tooltip>
      );
    }
    return (
      <Button onClick={onClickLabelProvider} theme='link'>
        {chunks}
      </Button>
    );
  };

  /**
   * Link component for demo store deactivation
   * @returns {React.ReactElement}
   */
  const DemoStoreDeactivateLink = () => {
    return (
      <Button onClick={onDeactivateDemoStoreClick} theme='link'>
        <Text textType={TextType.Paragraph} value={messages.deactivateDemoStore} />
      </Button>
    );
  };

  /**
   * Link component for wallet/ShipStation carriers
   * @param {object} props
   * @param {() => void} props.onClick - Click handler for the link
   * @returns {React.ReactElement}
   */
  const WalletLink = ({ onClick }) => {
    if (!userHasConfigurationRole) {
      return (
        <Tooltip>
          <Text className={style['disabled-text']} element='span' value={messages.enableShipStationCarriers} />
          <TooltipContent>
            <Text value={messages.contactAdmin} />
          </TooltipContent>
        </Tooltip>
      );
    }
    return (
      <Button onClick={onClick} theme='link'>
        <Text textType={TextType.Paragraph} value={messages.enableShipStationCarriers} />
      </Button>
    );
  };

  if (hasStoreConnected) {
    return <StoreConnectedAwaitingShipmentPlaceholder />;
  }
  return (
    <AutomationRulesProvider>
      <FilterEditorContextProvider>
        <div className={style['placeholder-background']}>
          <div className={style['no-stores-placeholder']}>
            <Text
              className={style['placeholder-title']}
              element='h1'
              textType={TextType.H2}
              value={isInDemoStore ? messages.inDemoStoreTitle : messages.awaitingShipmentTitle}
            />
            <Text
              className={style['demo-subtitle']}
              textType={TextType.Description}
              value={isInDemoStore ? messages.inDemoStoreDescription : messages.getStarted}
            />
            {isInDemoStore ? (
              <Text className={style['demo-try-it-out']} textType={TextType.Description} value={messages.inDemoStoreTryItOut} />
            ) : null}

            <AwaitingShipmentPlaceholderActions isInDemoStore={isInDemoStore} />

            {!isInDemoStore && (
              <Text
                className={style['placeholder-links']}
                formattedMessageValues={{
                  firstLink: (
                    <WalletRegistrationOrUpdateButton trackingId={EventSourceStrings.ORDERS_GRID_EMPTY_STATE_WALLET_REGISTRATION}>
                      {({ openModal, isWalletRegistered }) => {
                        if (homeCountryIsUS || isWalletRegistered) {
                          return (
                            <Button onClick={onClickRateBrowserOpen} theme='link'>
                              <Text textType={TextType.Paragraph} value={messages.seeOurRates} />
                            </Button>
                          );
                        }
                        return <WalletLink onClick={openModal} />;
                      }}
                    </WalletRegistrationOrUpdateButton>
                  ),
                  carrierLink: (chunks) => <CarrierLink chunks={chunks} />,
                }}
                textType={TextType.Paragraph}
                value={messages.notReadyToAddOrders}
              />
            )}

            {isInDemoStore ? <DemoStoreDeactivateLink /> : null}
          </div>
        </div>
        {isOpen ? <RateBrowserModalPortalWrapper close={onRateBrowserClose} launchRateBrowserVariant={CONTROL} /> : null}
      </FilterEditorContextProvider>
    </AutomationRulesProvider>
  );
};

/**
 * Wrapper component for the awaiting shipment placeholder with context providers
 * @param {object} props
 * @param {boolean} [props.hasStoreConnected] - Whether a store is connected
 * @returns {React.ReactElement}
 */
export const AwaitingShipmentPlaceholderWrapper = ({ hasStoreConnected = false }) => {
  const onProviderRegistrationFailure = useCallback(() => {
    NotificationService.showNotification({
      notificationType: NOTIFICATION_TYPE_ERROR,
      title: <FormattedMessage {...messages.fulfillmentProviderErrorTitle} />,
      content: <FormattedMessage {...messages.labelProviderError} />,
    });
  }, []);

  return (
    <ConnectLabelProviderModalContextProvider setCuiError={onProviderRegistrationFailure}>
      <AwaitingShipmentPlaceholder hasStoreConnected={hasStoreConnected} />
    </ConnectLabelProviderModalContextProvider>
  );
};
